// One trend series from one payload, at that payload's own granularity.
//
// The dashboard draws TWO trend charts and lets each be grouped independently, so
// this is called twice. It is one function called twice rather than two builders,
// for the usual reason: the second copy is where they start to disagree.
//
// It must be given a payload FETCHED at the same granularity it is asked to
// bucket. That is not a fussy precondition, it is the whole reason the two charts
// need two payloads:
//
// A payload holds whole weeks as single pre-summed rows plus raw days for its
// ragged edge, and which weeks are whole depends on the granularity it was planned
// for. A YTD payload planned at Month withholds the six month-straddling weeks as
// raw days -- correct for Month, but when the same payload is asked for a Week
// series, forWindow filters those raw days against the WEEK split's edge-day list,
// which does not contain them, and they are dropped. The data is present and still
// discarded: 29 weekly points where there should be 35, silently.
//
// An earlier attempt tried to pick a single granularity clever enough to serve
// both. It cannot be done by filtering, because the payload's shape -- not just
// its date coverage -- is what differs. Two payloads is the honest answer, and
// React Query collapses them into one request whenever the two granularities
// happen to be equal, which is the default and the common case.

import { useMemo } from "react";
import type { FilterState, Granularity } from "@/types/filters";
import { buildTrendFromRollup } from "@/lib/rollupTrend";
import { deriveTrendPoint, type TrendPoint } from "@/lib/trendMetrics";
import { splitRange } from "@/lib/rollupSplit";
import { getPeriodFamily, type DateRange } from "@/lib/periods";
import type { RollupPayload } from "@/hooks/useRollupData";

export function useRollupTrend(
  payload: RollupPayload | null,
  filters: FilterState,
  /** MUST be the granularity `payload` was fetched at. */
  granularity: Granularity
): TrendPoint[] | null {
  const {
    customStart,
    customEnd,
    period,
    scope,
    appointmentTypes,
    excludeZeroApptDays,
    drillLevel,
  } = filters;
  return useMemo(() => {
    if (!payload) return null;

    const family = getPeriodFamily(period);
    const range: DateRange = { startDate: customStart, endDate: customEnd };
    const split = splitRange(range, granularity, family);
    const weeks = new Set(split.wholeWeeks);
    const days = new Set(split.edgeDays);

    const buckets = buildTrendFromRollup({
      siteWeeks: payload.siteWeeks.filter((r) => weeks.has(String(r.week_start))),
      dvmWeeks: payload.dvmWeeks.filter((r) => weeks.has(String(r.week_start))),
      edgeRows: payload.edgeRows.filter((r) => days.has(r.schedule_date)),
      range,
      granularity,
      family,
      scope,
      appointmentTypes,
      excludeZeroApptDays,
      // Same condition the rest of the rollup path uses. The trend is always taken
      // at the request's own scope, never wider, so it can read the wide doctor
      // rows whenever they were fetched.
      measuresFromDvmWeeks: drillLevel === "dvm" || scope.resourceId !== null,
    });

    const typeFilterActive = appointmentTypes.length > 0;
    return buckets.map((b) => deriveTrendPoint(b, granularity, family, range, typeFilterActive));
  }, [
    payload,
    granularity,
    customStart,
    customEnd,
    period,
    scope,
    appointmentTypes,
    excludeZeroApptDays,
    drillLevel,
  ]);
}
